import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Save, X } from 'lucide-react';

import { VerificationStatus, UserRole } from 'utils/bankStatements';

export const TransactionEditor = ({
  transaction,
  statementPeriod,
  onCancel,
  onSave,
  userRole,
  focusField,
}) => {
  const [editedTransaction, setEditedTransaction] = useState(transaction);
  const [isSaving, setIsSaving] = useState(false);
  const dateInputRef = useRef(null);
  const amountInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const [amountInput, setAmountInput] = useState('');
  const [validationErrors, setValidationErrors] = useState({
    date: null,
    amount: null,
    type: null,
    description: null,
  });

  useEffect(() => {
    // First determine type from the actual amounts
    const type = transaction.depositAmount
      ? 'Deposit'
      : transaction.withdrawalAmount
        ? 'Withdrawal'
        : 'Unknown';

    setEditedTransaction(prev => ({
      ...prev,
      type,
    }));

    // Then set amount based on the determined type, not transaction.type
    const amount = type === 'Withdrawal'
      ? transaction.withdrawalAmount
      : type === 'Deposit'
        ? transaction.depositAmount
        : null;

    const initialAmount = amount !== null && amount !== undefined
      ? Math.abs(amount).toFixed(2)
      : '';

    setAmountInput(initialAmount);
  }, []);

  useEffect(() => {
    switch (focusField) {
      case 'date':
        dateInputRef.current?.focus();
        break;
      case 'amount':
        amountInputRef.current?.focus();
        break;
      case 'description':
        descriptionInputRef.current?.focus();
        break;
      default:
        break;
    }
  }, [focusField]);

  const validateDate = (date) => {
    const inputDate = new Date(date);
    if (!date || Number.isNaN(inputDate.getTime())) {
      return 'Please enter a valid date';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (inputDate > today) {
      return 'Cannot enter future dates';
    }

    if (statementPeriod && statementPeriod.startDate && statementPeriod.endDate) {
      const periodStart = new Date(statementPeriod.startDate);
      const periodEnd = new Date(statementPeriod.endDate);

      if (!Number.isNaN(periodStart.getTime()) && !Number.isNaN(periodEnd.getTime())) {
        if (inputDate < periodStart || inputDate > periodEnd) {
          return `Date must be between ${statementPeriod.startDate} and ${statementPeriod.endDate}`;
        }
      }
    }

    const minDate = new Date('1900-01-01');
    if (inputDate < minDate) {
      return 'Date cannot be before 1900';
    }

    return null;
  };

  const validateTransaction = () => {
    const errors = {
      date: validateDate(editedTransaction.date),
      amount: null,
      type: null,
      description: null,
    };

    const amount = parseFloat(amountInput);
    if (!amountInput || Number.isNaN(amount) || amount <= 0) {
      errors.amount = 'Please enter a valid amount greater than 0';
    }

    if (!editedTransaction.type || editedTransaction.type === 'Unknown') {
      errors.type = 'Please select a transaction type';
    }

    if (userRole !== UserRole.User && !editedTransaction.description) {
      errors.description = 'Please enter a description';
    }

    setValidationErrors(errors);
    return !errors.date && !errors.amount && !errors.type && (!errors.description || userRole === UserRole.User);
  };

  const handleSave = async () => {
    if (!validateTransaction()) {
      return;
    }

    setIsSaving(true);
    try {
      const amount = parseFloat(amountInput);
      const updatePayload = {
        ...editedTransaction,
        withdrawalAmount:
          editedTransaction.type === 'Withdrawal' ? amount : null,
        depositAmount: editedTransaction.type === 'Deposit' ? amount : null,
        verificationStatus: VerificationStatus.Manual,
        lastVerifiedAt: new Date().toISOString(),
        dateConfidence: 1,
        descriptionConfidence: 1,
        amountConfidence: 1,
        depositAmountConfidence:
          editedTransaction.type === 'Deposit' ? 1 : undefined,
        withdrawalAmountConfidence:
          editedTransaction.type === 'Withdrawal' ? 1 : undefined,
      };
      onSave(updatePayload);
    } catch (error) {
      console.error('Failed to save transaction:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      handleSave();
    }
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setEditedTransaction(prev => ({
      ...prev,
      date: newDate,
    }));

    if (newDate.length === 10) {
      const dateError = validateDate(newDate);
      setValidationErrors(prev => ({ ...prev, date: dateError }));
    } else {
      setValidationErrors(prev => ({ ...prev, date: null }));
    }
  };

  const commonInputStyles = {
    border: '1px solid #E5E7EB',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '14px',
    lineHeight: '20px',
    color: '#374151',
    width: '100%',
    backgroundColor: 'white',
  };

  const buttonBaseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
  };

  return (
    <tr style={{ backgroundColor: '#EFF6FF' }} onKeyDown={handleKeyDown}>
      <td style={{ padding: '8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <input
            ref={dateInputRef}
            type="date"
            value={editedTransaction.date}
            onChange={handleDateChange}
            max={new Date().toISOString().split('T')[0]}
            style={{
              ...commonInputStyles,
              borderColor: validationErrors.date ? '#EF4444' : '#E5E7EB',
              width: '140px',
              height: '32px',
            }}
          />
          {validationErrors.date && (
            <div style={{ fontSize: '12px', color: '#EF4444' }}>
              {validationErrors.date}
            </div>
          )}
        </div>
      </td>
      <td style={{ padding: '8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div
            style={{
              display: 'flex',
              gap: '1px',
              backgroundColor: '#E5E7EB',
              borderRadius: '6px',
              padding: '1px',
              border: validationErrors.type
                ? '1px solid #EF4444'
                : '1px solid transparent',
              width: '100%',
              height: '32px',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            }}
          >
            {['Withdrawal', 'Deposit'].map((type) => {
              const isSelected = editedTransaction.type === type;
              return (
                <button
                  type="button"
                  key={type}
                  onClick={() => {
                    setEditedTransaction(prev => ({
                      ...prev,
                      type,
                    }));
                    setValidationErrors(prev => ({ ...prev, type: null }));
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected
                      ? 'white'
                      : '#F3F4F6';
                    e.currentTarget.style.color = isSelected
                      ? '#1D4ED8'
                      : '#4B5563';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected
                      ? 'white'
                      : 'transparent';
                    e.currentTarget.style.color = isSelected
                      ? '#2563EB'
                      : '#6B7280';
                    e.currentTarget.style.transform = 'none';
                  }}
                  style={{
                    flex: 1,
                    border: 'none',
                    borderRadius: '5px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    backgroundColor: isSelected ? 'white' : 'transparent',
                    color: isSelected ? '#2563EB' : '#6B7280',
                    boxShadow: isSelected
                      ? '0 1px 2px rgba(0, 0, 0, 0.05)'
                      : 'none',
                    padding: '0 8px',
                    outline: 'none',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '30px',
                  }}
                >
                  {type}
                </button>
              );
            })}
          </div>
          {validationErrors.type && (
            <div style={{ fontSize: '12px', color: '#EF4444' }}>
              {validationErrors.type}
            </div>
          )}
        </div>
      </td>
      <td style={{ padding: '8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <input
            ref={amountInputRef}
            type="text"
            value={amountInput}
            onChange={(e) => {
              // First, remove any non-numeric and non-decimal characters
              let value = e.target.value.replace(/[^0-9.]/g, '');

              // Handle decimal places
              const parts = value.split('.');
              if (parts.length > 2) {
                // More than one decimal point - keep only first one
                value = `${parts[0]}.${parts.slice(1).join('')}`;
              }
              if (parts.length === 2 && parts[1].length > 2) {
                // More than 2 decimal places - truncate
                value = `${parts[0]}.${parts[1].slice(0, 2)}`;
              }

              setAmountInput(value);
              setValidationErrors(prev => ({ ...prev, amount: null }));
            }}
            style={{
              ...commonInputStyles,
              textAlign: 'right',
              borderColor: validationErrors.amount ? '#EF4444' : '#E5E7EB',
            }}
            placeholder="0.00"
          />
          {validationErrors.amount && (
            <div style={{ fontSize: '12px', color: '#EF4444' }}>
              {validationErrors.amount}
            </div>
          )}
        </div>
      </td>
      <td style={{ padding: '8px' }}>
        {userRole !== UserRole.User ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <input
              ref={descriptionInputRef}
              type="text"
              value={editedTransaction.description}
              onChange={(e) => setEditedTransaction({ ...editedTransaction, description: e.target.value })}
              style={{
                ...commonInputStyles,
                textAlign: 'right',
                borderColor: validationErrors.description ? '#EF4444' : '#E5E7EB',
              }}
            />
            {validationErrors.description && (
              <div style={{ fontSize: '12px', color: '#EF4444' }}>
                {validationErrors.description}
              </div>
            )}
          </div>
        ) : (
          <span
            style={{
              ...commonInputStyles,
              display: 'block',
              backgroundColor: '#F9FAFB',
              border: 'none',
            }}
          >
            {transaction.description}
          </span>
        )}
      </td>
      <td style={{ padding: '8px' }}>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            backgroundColor: '#FEF9C3',
            color: '#854D0E',
            borderRadius: '9999px',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          Editing
        </span>
      </td>
      <td style={{ padding: '8px' }}>
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            style={{
              ...buttonBaseStyles,
              backgroundColor: '#2563EB',
              color: 'white',
            }}
            title="Save changes"
          >
            {isSaving ? (
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid white',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
            ) : (
              <Save style={{ width: '16px', height: '16px' }} />
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{
              ...buttonBaseStyles,
              backgroundColor: '#F3F4F6',
              color: '#6B7280',
            }}
            title="Cancel editing"
          >
            <X aria-label="Cancel editing" style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      </td>
    </tr>
  );
};

TransactionEditor.propTypes = {
  transaction: PropTypes.object.isRequired,
  statementPeriod: PropTypes.shape({
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
  }),
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  focusField: PropTypes.string,
};

export default TransactionEditor;
